/**
 * What the Engine set, who accepted it, and how to put it back
 * (round 10 C5).
 *
 * Fifth round for this, and the harder half already landed: Undo
 * restores all five fields -- judgement, maturity, summary, gaps, and
 * the Engine's recommendations paragraph. What it did not do was
 * OUTLIVE ITS TOAST. Nine seconds after accepting, measured directly
 * rather than inferred, there was no Undo anywhere on the drawer, and
 * the record above three paragraphs she did not write said only
 * "Graded by Dana Preparer."
 *
 * The persona's stated fear is Engine output she cannot explain to a
 * reviewer. A toast that takes the explanation away after nine seconds
 * is the product removing it from the record.
 *
 * So the acceptance is held here -- outside both components, because
 * the panel that ACCEPTS and the panel that READS are siblings -- until
 * the control is saved again. Plain data, not a closure: a callback
 * captured at accept time would outlive the component that made it and
 * fire a mutation from a tree that no longer exists.
 *
 * DELIBERATELY NOT PERSISTED. `derived_from_run_id` is the durable
 * record that the Engine had a hand in the grade, written on accept
 * since round 9 C3 and read back by the review panel. This is the
 * narrower claim -- "and it was THIS session, and here is what it
 * replaced" -- which is only true while the thing it replaced is still
 * known.
 */
import { useSyncExternalStore } from "react";

export interface AcceptedSuggestion {
  runId: string;
  acceptedBy: string;
  /** ISO. */
  acceptedAt: string;
  /** What the control held before the Engine's suggestion replaced it. */
  previous: {
    status: string;
    current_ml: number | null;
    evidence_summary: string | null;
    gaps: string | null;
    recommendations: string | null;
  } | null;
}

const held = new Map<string, AcceptedSuggestion>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export function recordAcceptance(controlId: string, a: AcceptedSuggestion): void {
  held.set(controlId, a);
  emit();
}

/** Called when the control is saved again -- by hand, or undone. */
export function clearAcceptance(controlId: string): void {
  if (held.delete(controlId)) emit();
}

/** Test seam: no test should inherit another's acceptance. */
export function resetAcceptances(): void {
  held.clear();
  emit();
}

export function useAcceptance(controlId: string | null): AcceptedSuggestion | null {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => (controlId ? (held.get(controlId) ?? null) : null),
    () => null,
  );
}
